'use client';

import { useEffect, useState } from 'react';
import { useConvexAuth, useQuery, useAction } from 'convex/react';
import { useTranslations } from 'next-intl';
import {
  ChatBubbleLeftRightIcon,
  CheckIcon,
  ChevronRightIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../convex/_generated/api';
import { Modal, Button, FormField, Select, Textarea } from '@/design-system';

export const SUPPORT_EMAIL = 'support@42a.ai';

const TOPIC_KEYS = ['question', 'bug', 'billing', 'feature', 'other'] as const;
type TopicKey = (typeof TOPIC_KEYS)[number];

const URGENCY_KEYS = ['low', 'normal', 'high', 'urgent'] as const;
type UrgencyKey = (typeof URGENCY_KEYS)[number];

const MAX_MESSAGE_LENGTH = 5000;

interface SupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Reopens the welcome intro (offered as a small link under the form). */
  onReplayIntro?: () => void;
}

export default function SupportModal({ isOpen, onClose, onReplayIntro }: SupportModalProps) {
  const t = useTranslations('support');
  // Gate on Convex auth, not Clerk's isSignedIn (sign-out race, see src/lib/authError.ts)
  const { isAuthenticated } = useConvexAuth();
  // Server-resolved from the caller's identity: the exact context the support
  // email will carry, so the chips can't disagree with what actually gets sent.
  const context = useQuery(api.users.getMySupportContext, isAuthenticated ? {} : 'skip');
  const sendSupportRequest = useAction(api.support.sendSupportRequest);

  const [topic, setTopic] = useState<TopicKey>('question');
  const [urgency, setUrgency] = useState<UrgencyKey>('normal');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  // Fresh form every time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setTopic('question');
      setUrgency('normal');
      setMessage('');
      setStatus('idle');
    }
  }, [isOpen]);

  const userEmail = context?.userEmail;
  const userName = context?.userName || userEmail;
  // Plan ids ("free" | "pro" | "enterprise") shown capitalized; not translated
  // because plan names are product names.
  const planLabel = context?.plan
    ? context.plan.charAt(0).toUpperCase() + context.plan.slice(1)
    : null;

  const handleSend = async () => {
    if (!message.trim() || status === 'sending') return;
    setStatus('sending');
    try {
      await sendSupportRequest({ topic, urgency, message: message.trim() });
      setStatus('sent');
    } catch (err) {
      console.error('Failed to send support request', err);
      setStatus('error');
    }
  };

  const chipClass =
    'inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border border-[var(--color-border)] bg-[var(--color-surface-hover)] text-[var(--color-text-secondary)]';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('title')}
      icon={<ChatBubbleLeftRightIcon className="w-6 h-6 text-[var(--color-lime)]" />}
      maxWidth="lg"
    >
      {status === 'sent' ? (
        <div className="text-center py-8 animate-fade-in">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full grid place-items-center bg-[var(--color-lime-muted)]">
            <CheckIcon className="w-7 h-7 text-[var(--color-lime)]" />
          </div>
          <h4 className="text-lg font-semibold mb-1.5" style={{ color: 'var(--color-text-primary)' }}>
            {t('sentTitle')}
          </h4>
          <p className="text-sm max-w-[40ch] mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
            {t('sentBody', { supportEmail: SUPPORT_EMAIL, userEmail: userEmail ?? '' })}
          </p>
          <Button variant="secondary" size="sm" className="mt-6" onClick={onClose}>
            {t('done')}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm -mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            {t('subtitle')}
          </p>

          {/* Direct email card */}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="flex items-center gap-3 rounded-xl border p-3.5 transition-colors border-[var(--color-border)] hover:border-[var(--color-lime)] bg-[var(--color-surface-elevated)]"
          >
            <EnvelopeIcon className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                {t('emailDirectly')}
              </p>
              <p className="text-sm font-semibold text-[var(--color-lime)] truncate">{SUPPORT_EMAIL}</p>
            </div>
            <ChevronRightIcon className="w-4 h-4 ml-auto flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
          </a>

          {/* Divider */}
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
            <div className="h-px flex-1" style={{ background: 'var(--color-border)' }} />
            {t('orMessage')}
            <div className="h-px flex-1" style={{ background: 'var(--color-border)' }} />
          </div>

          {/* Auto-attached context */}
          <div>
            <div className="flex flex-wrap gap-1.5">
              {userName && (
                <span className={chipClass}>
                  <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{userName}</span>
                  {userEmail && userEmail !== userName && <span>{userEmail}</span>}
                </span>
              )}
              {context?.organizationName && (
                <span className={chipClass}>{t('organization', { name: context.organizationName })}</span>
              )}
              {planLabel && <span className={chipClass}>{t('plan', { name: planLabel })}</span>}
            </div>
            <p className="text-[11px] mt-1.5" style={{ color: 'var(--color-text-muted)' }}>
              {t('contextNote')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={t('topicLabel')}>
              <Select<TopicKey>
                value={topic}
                onChange={(value) => setTopic(value as TopicKey)}
                options={TOPIC_KEYS.map((key) => ({ value: key, label: t(`topics.${key}`) }))}
                portal
              />
            </FormField>

            <FormField label={t('urgencyLabel')}>
              <Select<UrgencyKey>
                value={urgency}
                onChange={(value) => setUrgency(value as UrgencyKey)}
                options={URGENCY_KEYS.map((key) => ({ value: key, label: t(`urgencies.${key}`) }))}
                portal
              />
            </FormField>
          </div>

          <FormField
            label={t('messageLabel')}
            error={status === 'error' ? t('error', { email: SUPPORT_EMAIL }) : undefined}
          >
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('messagePlaceholder')}
              maxLength={MAX_MESSAGE_LENGTH}
              rows={4}
            />
          </FormField>

          <Button
            className="w-full shadow-glow-lime"
            onClick={handleSend}
            loading={status === 'sending'}
            disabled={!message.trim()}
          >
            {t('send')}
          </Button>

          {onReplayIntro && (
            <p className="text-center text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {t('replayPrompt')}{' '}
              <button
                type="button"
                onClick={onReplayIntro}
                className="text-[var(--color-lime)] underline underline-offset-4 hover:text-[var(--color-lime-hover)] transition-colors"
              >
                {t('replayLink')}
              </button>
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
